import React, { FunctionComponent, useState } from 'react';
import * as styles from '../ExportToFile.module.scss';
import { CreateExtractedDataExportParams } from '../ExportToFile';
import { Stack } from '../../../../components/layout';
import { BiasWarning } from './BiasWarning';
import { Button, Heading, FormLabel, Select } from 'components/core';

type De2ExtractionFormProps = {
  createExport: (type: string, params: CreateExtractedDataExportParams) => void;
  isSingleExtractor: boolean;
};

/* The ExportOptionValue must match the enum options in app/concepts/export/individual_reviewer_form_export.rb */
type ExportOptionValue =
  | 'all'
  | 'consensusOnly'
  | 'deConsensusOnly'
  | 'qaConsensusOnly'
  | 'deConsensusIndividualData'
  | 'qaConsensusIndividualData';

type ExportOption = {
  value: ExportOptionValue;
  description: string;
  warn: boolean;
};

const exportOptionDeConsensusOnly: ExportOption = {
  value: 'deConsensusOnly',
  description: 'Data extraction consensus only (default)',
  warn: false,
};

const exportOptionQaConsensusOnly: ExportOption = {
  value: 'qaConsensusOnly',
  description: 'Quality assessment consensus only',
  warn: false,
};

const exportOptionAll: ExportOption = {
  value: 'all',
  description: 'All data',
  warn: true,
};

const exportOptions: ExportOption[] = [
  exportOptionDeConsensusOnly,
  exportOptionQaConsensusOnly,
  {
    value: 'deConsensusIndividualData',
    description: 'Data extraction consensus + individual data',
    warn: true,
  },
  {
    value: 'qaConsensusIndividualData',
    description: 'Quality assessment consensus + individual data',
    warn: true,
  },
  {
    value: 'consensusOnly',
    description: 'Data extraction & Quality assessment consensus only',
    warn: false,
  },
  exportOptionAll,
];

const getExportOption = (value: ExportOption['value']) => {
  const matchingOption = exportOptions.find((option) => option.value === value);
  return matchingOption ?? exportOptionAll;
};

const De2ExtractionForm: FunctionComponent<De2ExtractionFormProps> = (
  props
) => {
  const MORE_INFO_TARGET = 'https://support.covidence.org/help/export';
  const { createExport, isSingleExtractor } = props;
  const exportType = 'extracted_data_for_new_extraction';

  const [
    selectedExportOption,
    setSelectedExportOption,
  ] = useState<ExportOption>(
    isSingleExtractor ? exportOptionAll : exportOptionDeConsensusOnly
  );

  const [format, setFormat] = useState<string>('csv');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    gtag('event', 'Export Extraction Data', {
      event_category: 'engagement',
      event_label: format,
    });
    createExport(exportType, {
      format,
      content_filter: selectedExportOption.value,
    });
  };

  const de2ExtractionContent = () => (
    <Stack spacing={5}>
      <div>
        <FormLabel htmlFor="extracted_export_data" size="sm">
          Data
        </FormLabel>
        <Select
          id="extracted_export_data"
          style={{ textOverflow: 'ellipsis' }}
          onChange={(e) => {
            const selectedOptionValue = e.target.value as ExportOptionValue;
            const selectedOption = getExportOption(selectedOptionValue);
            setSelectedExportOption(selectedOption);
          }}
          className="form-control select"
          aria-describedby="biasWarning"
        >
          {exportOptions.map(({ value, description }) => (
            <option key={value} value={value}>
              {description}
            </option>
          ))}
        </Select>
      </div>
      <div>
        <FormLabel htmlFor="extracted_export_format" size="sm">
          Format
        </FormLabel>
        <Select
          id="extracted_export_format"
          value={format}
          className="form-control select"
          onChange={(e) => setFormat(e.target.value)}
        >
          <option value="csv">Single sheet (CSV)</option>
          <option value="xlsx">Single sheet (Excel)</option>
        </Select>
      </div>
      {selectedExportOption.warn && (
        <div role="alert" id="biasWarning">
          <BiasWarning moreInfoTarget={MORE_INFO_TARGET} />
        </div>
      )}
    </Stack>
  );

  return (
    <form
      id="export-extracted-data"
      onSubmit={handleSubmit}
      className={styles.exportForm}
    >
      <div className={styles.exportFormHeader}>
        <Heading type="h5">Extraction</Heading>
      </div>
      <Stack spacing={5} className={styles.exportFormContent}>
        {de2ExtractionContent()}
        <div className={styles.exportFormFooter}>
          <Button className={styles.exportFormButton} type="brand">
            Prepare file
          </Button>
        </div>
      </Stack>
    </form>
  );
};

export default De2ExtractionForm;
